import * as React from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SEMANTIC } from '@/lib/theme'

export interface RecordComboboxProps<T extends { id: string }> {
  records: T[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  displayField: keyof T | ((record: T) => string)
  disabled?: boolean
  className?: string
  'aria-label'?: string
  id?: string
  onSearchChange?: (search: string) => void
  isLoading?: boolean
  selectedLabel?: string
  /** Total count from API — if greater than records.length, shows a truncation warning */
  totalCount?: number
}

export function RecordCombobox<T extends { id: string }>({
  records,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  displayField,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  id,
  onSearchChange,
  isLoading = false,
  selectedLabel: selectedLabelProp,
  totalCount,
}: RecordComboboxProps<T>) {
  const [open, setOpen] = React.useState(false)

  const getDisplayValue = React.useCallback(
    (record: T): string => {
      if (typeof displayField === 'function') {
        return displayField(record)
      }
      return String(record[displayField] ?? '')
    },
    [displayField]
  )

  const selectedRecord = React.useMemo(
    () => records.find((record) => record.id === value),
    [records, value]
  )

  const selectedLabel = selectedRecord ? getDisplayValue(selectedRecord) : selectedLabelProp || null

  const handleSelect = React.useCallback(
    (recordId: string) => {
      if (recordId === 'none') {
        onValueChange('')
      } else {
        onValueChange(recordId === value ? '' : recordId)
      }
      setOpen(false)
    },
    [onValueChange, value]
  )

  const popoverId = id ? `${id}-popover` : undefined
  const listboxId = id ? `${id}-listbox` : undefined

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen && onSearchChange) {
          onSearchChange('')
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedLabel && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        id={popoverId}
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={!onSearchChange}>
          <CommandInput placeholder={searchPlaceholder} onValueChange={onSearchChange} />
          <CommandList
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            className="max-h-[400px]"
          >
            {isLoading ? (
              <div className="py-6 text-center text-sm" role="status" aria-live="polite">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="sr-only">Loading results</span>
              </div>
            ) : (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            <CommandGroup>
              <CommandItem
                value="none"
                onSelect={() => handleSelect('none')}
                className="text-muted-foreground"
              >
                <Check
                  className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')}
                  aria-hidden="true"
                />
                None
              </CommandItem>
              {records.map((record) => {
                const label = getDisplayValue(record)
                return (
                  <CommandItem
                    key={record.id}
                    value={label}
                    onSelect={() => handleSelect(record.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === record.id ? 'opacity-100' : 'opacity-0'
                      )}
                      aria-hidden="true"
                    />
                    {label}
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {totalCount !== undefined && totalCount > records.length && (
              <div
                className="px-3 py-2 text-xs border-t"
                style={{
                  backgroundColor: SEMANTIC.warning.fill,
                  color: SEMANTIC.warning.text,
                }}
                role="status"
              >
                Showing {records.length} of {totalCount.toLocaleString()} results. Refine your
                search to see more.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
